import { BasePlot } from './base_plot'
import { select, selectAll, event } from 'd3-selection'
import { ScrollingCanvasPlot } from './scrolling_canvas_plot'
import { range } from 'd3-array'
import { randomNormal } from 'd3-random'
import { line } from 'd3-shape'
import { ndarray } from 'ndarray'
import { scaleLinear, scaleOrdinal } from 'd3-scale'
import { schemeCategory10 } from 'd3-scale-chromatic'
import { axisBottom, axisLeft } from 'd3-axis'
import 'd3-transition'
import { easeLinear } from 'd3-ease'
import { color, Rgb } from 'd3-color'
import { brush } from 'd3-brush'
import { transition } from 'd3-transition'
import { legend, legendColor } from 'd3-svg-legend'
import * as request from 'd3-request' // d3 submodule (contains d3.csv, d3.json, etc)

// create a Object with only the subset of functions/submodules/plugins that we need
const d3 = Object.assign(
    {},
    {
        select,
        selectAll,
        range,
        line,
        easeLinear
    },
    request
);

export class TimePlot extends ScrollingCanvasPlot{

    private ctx: CanvasRenderingContext2D;
    private canvasHeight: number;
    private canvasWidth: number;
    private colorScale;
    private data;
    private yScaleCanvas;
    private xScaleCanvas;

    private xScale;
    private yScale;
    private xAxis;
    private yAxis;
    private xAxisG;
    private yAxisG;

    private n: number;
    private lastCol: Array<number>;
    private lastCol2: Array<number>;
    private brush;

    public constructor(key: string, container: HTMLElement, numChannels: number, numPoints: number, yLim: Array<number>, config: Object = {}) {
        super(key, container, 0, 0);
        this.numChannels = numChannels;
        this.container = container
        this.n = numPoints;


        this.xScale = scaleLinear()
            .domain([0, this.n - 1])
            .range([this.screenWidth-1, 0]);
        this.yScale = scaleLinear()
            .domain([yLim[0], yLim[1]])
            .range([this.screenHeight-1, 0]);

        this.xScaleCanvas = scaleLinear()
            .domain([0, this.n - 1])
            .range([this.width-1, 0]);
        this.yScaleCanvas = scaleLinear()
            .domain([yLim[0], yLim[1]])
            .range([this.height-1, 0]);

        this.colorScale = scaleOrdinal(schemeCategory10).domain(Array.apply(null, {length: numChannels}).map(Number.call, Number))

        this.xAxis = axisBottom(this.xScale).ticks(4) //specify number of ticks ;
        this.xAxisG = this.svg.append("g")
            .attr("class", "axis axis--x")
            .attr("transform", "translate(0," + this.yScale(0) + ")")
            .call(this.xAxis);
        this.yAxis = axisLeft(this.yScale)
        this.yAxisG = this.svg.append("g")
            .attr("class", "axis axis--y")
            .call(this.yAxis);

        if ("title" in config){
          this.svg.append("text")
            .attr("x", (this.width / 2))
            .attr("y", -2)
            .attr("text-anchor", "middle")
            .style("font-size", "16px")
            .text(config.title);
        }

        if ("yLabel" in config){
          this.svg.append("text")
              .attr("transform", "rotate(-90)")
              .attr("y", 0 - this.margin.left)
              .attr("x",0 - (this.screenHeight / 2))
              .attr("dy", "1em")
              .style("text-anchor", "middle")
              .text(config.yLabel);
        }
        if ("labels" in config){
          this.svg.append("g")
            .attr("class", "legendSequential")
            .attr("transform", "translate(20,20)");

          var legendSequential = legendColor()
              .shapeWidth(10)
              .shapeHeight(10)
              // .cells(10)
              // .orient("horizontal")
              .labels(config.labels)
              .scale(this.colorScale)

          this.svg.select(".legendSequential")
            .call(legendSequential);
          }

        this.brush = brush()
          .extent( [ [0,0], [this.screenWidth,this.screenHeight] ] )
          .on("end", () => this.brushed())

        this.svg.append("g")
          .attr("class", "brush")
          .call(this.brush);

        this.svg.on("dblclick",() => {
              // console.log(this, this.xScale, this.xScale.domain());
              this.xScale.domain([0, this.n - 1]);
              this.yScale.domain([yLim[0], yLim[1]]);
              this.xScaleCanvas.domain([0, this.n - 1]);
              this.yScaleCanvas.domain([yLim[0], yLim[1]]);
              this.xAxisG.transition().call(this.xAxis);
              this.yAxisG.transition().call(this.yAxis);
              this.svg.select(".brush").call(this.brush.move, null);
              this.ctx.clearRect(0,0,this.width, this.height);
            });

        this.lastCol = d3.range(numChannels).map((_d) => 0);
        this.lastCol2 = d3.range(numChannels).map((_d) => 0);

    }
    private brushed(){
      console.log(event)
      if (event.type == "end"){
        let s = event.selection;
        if (!s){

        } else {
          let newX = [s[1][0], s[0][0]].map(this.xScale.invert, this.xScale);
          let newY = [s[1][1], s[0][1]].map(this.yScale.invert, this.yScale);
          newX[0] = 0;
          console.log(newX, newY);
          this.xScale.domain(newX);
          this.yScale.domain(newY);
          this.xScaleCanvas.domain(newX);
          this.yScaleCanvas.domain(newY);
          this.svg.select(".brush").call(this.brush.move, null);
          this.ctx.clearRect(0,0,this.width, this.height);
        }

        let yRange = this.yScale.domain()[1] - this.yScale.domain()[0];
        let xRange = this.xScale.domain()[0] - this.xScale.domain()[1];
        let yZoomFactor = this.maxFreq / yRange;
        let xZoomFactor = this.width / xRange;
        let height = yZoomFactor*this.screenHeight;
        let width = xZoomFactor*this.screenWidth;
        let top = (this.maxFreq - this.yScale.domain()[1]) * this.screenHeight / yRange;
        let left = 0;
        // console.log(yRange, yZoomFactor, height, top);
        console.log(xRange, xZoomFactor, width, left);

        // var t = this.svg.transition().duration(750);
        this.xAxisG.transition().call(this.xAxis);
        this.yAxisG.transition().call(this.yAxis);
        // this.yAxis.transition().call(this.yAxis);
        // svg.selectAll("circle").transition(t)
        //     .attr("cx", function(d) { return x(d[0]); })
        //     .attr("cy", function(d) { return y(d[1]); });
      }
    }

    private shiftCanvasLeft(pixels: number){
      this.ctx.globalCompositeOperation = "copy";
      this.ctx.drawImage(this.ctx.canvas, this.xScaleCanvas(this.xScaleCanvas.domain()[1]+pixels), 0);
      this.ctx.globalCompositeOperation = "source-over";
    }

    private drawNewColumns(colData: Array<Array<number>>){
      const numCols: number = colData.length;
      this.ctx.lineWidth = 3;
      for (let i = 0; i < this.numChannels; i++) {
        // this.ctx.globalCompositeOperation = "source-out";
        this.ctx.beginPath();
        this.ctx.moveTo(this.xScaleCanvas(numCols+2), this.yScaleCanvas(this.lastCol2[i]));
        this.ctx.lineTo(this.xScaleCanvas(numCols+1), this.yScaleCanvas(this.lastCol[i]));

        // this.ctx.globalCompositeOperation = "source-over";
        for (let x = 0; x < numCols; x++) {
          this.ctx.lineTo(this.xScaleCanvas(numCols-x), this.yScaleCanvas(colData[x][i]));
        }
        this.ctx.strokeStyle = this.colorScale(i);
        this.ctx.stroke();
      }
      if (numCols >= 2) {
        this.lastCol2 = colData[numCols-2];
      } else {
        this.lastCol2 = this.lastCol
      }
      this.lastCol = colData[numCols-1];
    }


}
